import { Locale } from 'shared/i18n/interface'
import type { Care } from 'shared/types/vp/care'
type CareTileApiPrice = {
  currency?: string
  discountedPrice?: number
  fractionDigits?: number
  listPrice?: number
  minimumFractionDigits?: number
  untaxedPrice?: number
}

const DEFAULT_FRACTION_DIGITS = 2

const isCareTileApiPrice = (value: unknown): value is CareTileApiPrice => typeof value === 'object' && value !== null

const getCareTilePriceAmount = (price: CareTileApiPrice, vatInclusive: boolean): number | undefined => {
  if (vatInclusive && typeof price.listPrice === 'number') {
    return price.listPrice
  }

  if (typeof price.untaxedPrice === 'number') {
    return price.untaxedPrice
  }

  if (typeof price.listPrice === 'number') {
    return price.listPrice
  }

  return undefined
}

const getCareTilePriceFractionDigits = (price: CareTileApiPrice): number =>
  typeof price.fractionDigits === 'number'
    ? price.fractionDigits
    : typeof price.minimumFractionDigits === 'number'
      ? price.minimumFractionDigits
      : DEFAULT_FRACTION_DIGITS

const formatCareTileCurrency = (locale: Locale, currency: string, amount: number, fractionDigits: number): string | null => {
  if (!Intl.NumberFormat.supportedLocalesOf([locale]).length) {
    return null
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount)
  } catch {
    return null
  }
}

export const getCareTilePricingText = (locale: Locale, price?: Care.DsServicePrice, vatInclusive: boolean = true): string | null => {
  if (!isCareTileApiPrice(price) || !price.currency) {
    return null
  }

  const amount = getCareTilePriceAmount(price, vatInclusive)

  if (amount === undefined || amount <= 0) {
    return null
  }

  return formatCareTileCurrency(locale, price.currency, amount, getCareTilePriceFractionDigits(price))
}
